import { AxiosError } from "axios";

/**
 * Turning an API refusal into something a form can show next to the right
 * field.
 *
 * Two shapes arrive. Pydantic sends a list with the field in `loc`, which is
 * exactly what a form needs. The database constraint handler sends one Croatian
 * sentence with no field attached — those are mapped here to the field they are
 * about, because a message at the top of a long form is a message somebody has
 * to hunt for.
 */

export interface PorukeGresaka {
  /** Field name → message, for the input it belongs to. */
  polja: Record<string, string>;
  /** What could not be attached to any one field. */
  opcenito: string[];
}

/** No answer from the server: the request never got one, or never left. */
export const PORUKA_VEZA = "Nema veze s poslužiteljem. Provjerite vezu.";
/** The server answered, and the answer was no. */
export const PORUKA_OVLAST = "Nemate ovlast za ove podatke.";
/** The server answered with something nobody can act on from a phone. */
export const PORUKA_POSLUZITELJ = "Pogreška na poslužitelju. Pokušajte ponovno.";

/**
 * Why a screen could not load its data, in words the person can act on.
 *
 * Three causes, three answers, and telling them apart is not cosmetic. An
 * account that may not read the data (O-11) shown "provjerite vezu" sends
 * somebody to restart the phone, walk out for signal and try again — and land
 * on the same screen, because nothing was ever wrong with the connection.
 *
 * A refusal is therefore never reported as a network problem, and the network
 * message is used only when we actually know the request got no answer: an
 * Axios error with no `response`. Anything else — a 5xx, a shape we did not
 * expect, an error thrown in our own code — falls to the server message, which
 * claims nothing about the connection or about the person's rights.
 */
export function porukaDohvata(error: unknown): string {
  if (!(error instanceof AxiosError)) {
    return PORUKA_POSLUZITELJ;
  }
  if (!error.response) {
    return PORUKA_VEZA;
  }
  return error.response.status === 403 ? PORUKA_OVLAST : PORUKA_POSLUZITELJ;
}

/**
 * Which field each constraint message concerns. The text comes from
 * `app/core/greske.py`; matching on a fragment rather than the whole sentence
 * survives a rewording that keeps the meaning.
 */
const PO_FRAGMENTU: [string, string][] = [
  ["OIB-om već postoji", "oib"],
  ["OIB mora imati", "oib"],
  ["kontrolna znamenka", "oib"],
  ["Datum prestanka i razlog prestanka", "datum_prestanka"],
  ["mora imati datum do kojeg vrijedi", "ugovor_vrijedi_do"],
  ["državljanstvo ne postoji", "drzavljanstvo_iso2"],
  ["radno mjesto ne postoji", "radno_mjesto_id"],
  ["Datum isteka dozvole", "datum_isteka"],
];

interface PydanticGreska {
  loc?: (string | number)[];
  msg?: string;
}

export function procitajGreske(error: unknown): PorukeGresaka {
  const rezultat: PorukeGresaka = { polja: {}, opcenito: [] };

  if (!(error instanceof AxiosError) || !error.response) {
    rezultat.opcenito.push(
      "Spremanje nije uspjelo. Provjerite vezu i pokušajte ponovno.",
    );
    return rezultat;
  }

  const detail: unknown = (error.response.data as { detail?: unknown })?.detail;

  if (Array.isArray(detail)) {
    for (const stavka of detail as PydanticGreska[]) {
      const polje = stavka.loc?.filter((dio) => dio !== "body").at(-1);
      const poruka = stavka.msg?.replace(/^Value error,\s*/, "") ?? "";
      if (typeof polje === "string" && poruka) {
        rezultat.polja[polje] = poruka;
      } else if (poruka) {
        rezultat.opcenito.push(poruka);
      }
    }
    return rezultat;
  }

  if (typeof detail === "string") {
    const pogodak = PO_FRAGMENTU.find(([fragment]) => detail.includes(fragment));
    if (pogodak) {
      rezultat.polja[pogodak[1]] = detail;
    } else {
      rezultat.opcenito.push(detail);
    }
    return rezultat;
  }

  rezultat.opcenito.push("Spremanje nije uspjelo. Pokušajte ponovno.");
  return rezultat;
}
